import { useEffect, useState, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { isEmpty, isNullish } from 'remeda';

import { Typography } from '@mui/material';
import { Box } from '@mui/system';

import { getIdConvertedTimestamp } from '@shared/utils';

import { useLazyGetMasterKeyByIdQuery, useLazyGetUserByIdQuery } from '@services';

import { AppDispatch, openPopover, RootState } from '@store';

import { MatchingOptionsEnum } from './types';

import { userAtom } from 'v2/atoms/userAtom';
import { UserRole } from 'v2/constants';

export const useUserPermissions = (
  rolesForMatching: UserRole[],
  option: MatchingOptionsEnum = MatchingOptionsEnum.SOME
) => {
  const user = useAtomValue(userAtom);

  if (isNullish(user) || isEmpty(user.roles)) {
    return false;
  }

  switch (option) {
    case MatchingOptionsEnum.SOME: {
      return user.roles.some((role) => rolesForMatching.includes(role));
    }
    case MatchingOptionsEnum.EVERY: {
      return user.roles.every((role) => rolesForMatching.includes(role));
    }
    default: {
      return false;
    }
  }
};

export const useModal = (initialState = false): [boolean, () => void, () => void] => {
  const [isOpened, setIsOpened] = useState(initialState);

  const handleModalOpen = () => {
    setIsOpened(true);
  };

  const handleModalClose = () => {
    setIsOpened(false);
  };

  return [isOpened, handleModalOpen, handleModalClose];
};

export const useServerDataGridRefresh = (
  refreshSelector: Selector<RootState, boolean>,
  refreshAction: ActionCreatorWithPayload<boolean>,
  refreshServerDataGrid: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const shouldRefreshTable = useSelector(refreshSelector);

  useEffect(() => {
    if (shouldRefreshTable) {
      refreshServerDataGrid();

      dispatch(refreshAction(false));
    }
  }, [shouldRefreshTable]);
};

export const usePopover = (
  getContent: (anchorElId: string, contentData?: any) => Promise<JSX.Element>
): {
  handleOpenPopover: (event: MouseEvent<HTMLElement>, data?: any) => void;
} => {
  const dispatch = useDispatch<AppDispatch>();

  const handleOpenPopover = async (event: MouseEvent<HTMLElement>, data?: any) => {
    const anchorElId = event.currentTarget.id;

    const content = await getContent(anchorElId, data);

    dispatch(
      openPopover({
        anchorElId,
        content,
        isOpen: true,
      })
    );
  };

  return { handleOpenPopover };
};

export const useUserPopover = (): {
  handleOpenPopover: (event: MouseEvent<HTMLElement>) => void;
} => {
  const [getUserById] = useLazyGetUserByIdQuery();

  const getContent = async (anchorElId: string) => {
    const { id, telegramUserName, userNickname } = await getUserById(anchorElId).unwrap();

    const registrationDate = getIdConvertedTimestamp(id);
    const convertedRegistrationDate = dayjs(registrationDate).format('DD.MM.YYYY HH:mm');

    return (
      <Box
        sx={{
          padding: '5px',
          display: 'flex',
          gap: '5px',
          flexDirection: 'column',
        }}
      >
        <Typography>
          User Id: <strong>{id}</strong>
        </Typography>
        <Typography>
          Telegram User Name: <strong>{telegramUserName}</strong>
        </Typography>
        <Typography>
          Nickname: <strong>{userNickname}</strong>
        </Typography>
        <Typography>
          Registration Date: <strong>{convertedRegistrationDate}</strong>
        </Typography>
      </Box>
    );
  };

  return usePopover(getContent);
};

export const useMasterKeyPopover = (): {
  handleOpenPopover: (event: MouseEvent<HTMLElement>) => void;
} => {
  const [getMasterKeyById] = useLazyGetMasterKeyByIdQuery();

  const getContent = async (anchorElId: string) => {
    const masterKey = await getMasterKeyById(anchorElId).unwrap();

    return (
      <Box
        sx={{
          padding: '5px',
          display: 'flex',
          gap: '5px',
          flexDirection: 'column',
        }}
      >
        <Typography>
          Name: <strong>{masterKey.name}</strong>
        </Typography>
        <Typography>
          Key: <strong>{masterKey.key}</strong>
        </Typography>
      </Box>
    );
  };

  return usePopover(getContent);
};
